import React, { useState } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";

import ConsoleOutline from "@druva-apps/orion-icons/lib/ConsoleGreyOutline";
import DeviceEndpointsOutline from "@druva-apps/orion-icons/lib/DeviceendpointsGreyOutline";
import SaasApps from "@druva-apps/orion-icons/lib/SaasappsGreyOutline";
import Microsoft365Outline from "@druva-apps/orion-icons/lib/Microsoft365GreyOutline";
import GsuiteOutline from "@druva-apps/orion-icons/lib/GoogleworkspaceGreyOutline";
import SalesforceOutline from "@druva-apps/orion-icons/lib/SalesforceGreyOutline";
import SlackOutline from "@druva-apps/orion-icons/lib/SlackGreyOutline";
import ShareOutline from "@druva-apps/orion-icons/lib/ShareGreyOutline";
import PhoenixOutline from "@druva-apps/orion-icons/lib/HybridworkloadsGreyOutline";
import CloudrangerOutline from "@druva-apps/orion-icons/lib/CloudcacheGreyOutline";
import Legalhold from "@druva-apps/orion-icons/lib/LegalholdGreyOutline";
import ComplianceOutline from "@druva-apps/orion-icons/lib/ComplianceGreyOutline";
import FederatedSearchOutline from "@druva-apps/orion-icons/lib/FederatedsearchGreyOutline";
import DataLossPreventionOutline from "@druva-apps/orion-icons/lib/DatalosspreventionGreyOutline";
import RansomwareOutline from "@druva-apps/orion-icons/lib/RansomwareGreyOutline";
import ReportsOutline from "@druva-apps/orion-icons/lib/ReportsGreyOutline";
import AdminGreyOutline from "@druva-apps/orion-icons/lib/AdminGreyOutline";
import Configuration from "@druva-apps/orion-icons/lib/SettingsGreyOutline";
import InfoOutline from "@druva-apps/orion-icons/lib/InfoGreyOutline";
import Button from "../../button/button";
import Modal from "../../modal/modal";
import SideNav from "../../sideNav/sideNav";
import Sidebar from "../../sidebar/sidebar";

const GlobalSidebarIconMap = {
  home: <ConsoleOutline inheritFontColor aria-hidden="true" />,
  endpoints: <DeviceEndpointsOutline inheritFontColor aria-hidden="true" />,
  "saas-apps": <SaasApps inheritFontColor aria-hidden="true" />,
  "m-365": <Microsoft365Outline inheritFontColor aria-hidden="true" />,
  "google-workspace": <GsuiteOutline inheritFontColor aria-hidden="true" />,
  salesforce: <SalesforceOutline inheritFontColor aria-hidden="true" />,
  slack: <SlackOutline inheritFontColor aria-hidden="true" />,
  share: <ShareOutline inheritFontColor aria-hidden="true" />,
  "hybrid-workloads": <PhoenixOutline inheritFontColor aria-hidden="true" />,
  "cloud-ranger": <CloudrangerOutline inheritFontColor aria-hidden="true" />,
  "e-discovery": <Legalhold inheritFontColor aria-hidden="true" />,
  "sensitive-data-governance": (
    <ComplianceOutline inheritFontColor aria-hidden="true" />
  ),
  "federated-search": (
    <FederatedSearchOutline inheritFontColor aria-hidden="true" />
  ),
  "data-loss": (
    <DataLossPreventionOutline inheritFontColor aria-hidden="true" />
  ),
  "ransomware-recovery": (
    <RansomwareOutline inheritFontColor aria-hidden="true" />
  ),
  reports: <ReportsOutline inheritFontColor muted={false} />,
  "manage-administrators": (
    <AdminGreyOutline inheritFontColor aria-hidden="true" />
  ),
  "cloud-settings": <Configuration inheritFontColor aria-hidden="true" />,
  "account-details": <InfoOutline inheritFontColor aria-hidden="true" />
};

const GlobalSidebarTemplate = props => {
  const [showFedrampNotification, setFedrampNotification] = useState(false);

  const {
    float,
    globalSidebarContent: { navBarSections },
    isFedrampCompliant,
    productName: currentProduct,
    footerLinks = {},
    dataTestId
  } = props;

  const { toggle } = float;

  const { privacyPolicyLink, supportLink } = footerLinks;

  const getSideNavItem = links => {
    return links.map(link => {
      const { displayName, productName, url, isLicensed, services, slug } =
        link;

      if (!url && services && services.length > 0) {
        return {
          name: displayName,
          icon: GlobalSidebarIconMap[slug],
          collapsible: true,
          items: getSideNavItem(services)
        };
      }

      return {
        name: displayName,
        href: url,
        icon: GlobalSidebarIconMap[slug],
        productFedrampCompliant:
          productName === "inSync" || productName === "realize"
            ? isFedrampCompliant
            : false,
        target: isLicensed ? "_self" : "_blank",
        isActive: currentProduct === displayName
        // TODO: Phoenix has another condition on target props.uxCustomizationInfo?.uiCustomizations.openProductsInSameTab
      };
    });
  };

  let sidebarSections = [{}];

  if (Array.isArray(navBarSections) && navBarSections.length) {
    sidebarSections = navBarSections.map(section => {
      const { heading, links } = section;
      return {
        heading,
        component: () => (
          <SideNav
            items={getSideNavItem(links)}
            onItemClick={(item, e) => {
              toggle();
              const { isActive, productFedrampCompliant } = item;
              if (isActive) {
                e.preventDefault();
                return;
              }
              if (productFedrampCompliant) {
                // realize also has the logic !isLicensed
                setFedrampNotification(true);
                e.preventDefault();
              }
            }}
            dataTestId={`globalSidebarSection${section.heading.replace(
              / /g,
              ""
            )}`}
          />
        )
      };
    });
  }

  return (
    <div data-testid={dataTestId}>
      <Sidebar
        sections={sidebarSections}
        float={float}
        footerLinks={
          !isEmpty(footerLinks)
            ? [
                {
                  href: privacyPolicyLink,
                  label: "Privacy Policy",
                  target: "_blank"
                },
                {
                  href: supportLink,
                  label: "Support",
                  target: "_blank"
                }
              ]
            : null
        }
      />
      <Modal
        isOpen={showFedrampNotification}
        toggle={() => {
          setFedrampNotification(!showFedrampNotification);
        }}
        size="sm"
        header="Service not available"
        body={() =>
          "The requested service is not FedRAMP certified. To learn more, contact Druva Support."
        }
        footer={() => (
          <Button
            label="Ok"
            type="primary"
            onClick={() => setFedrampNotification(false)}
          />
        )}
      />
    </div>
  );
};

GlobalSidebarTemplate.propTypes = {
  float: PropTypes.objectOf(PropTypes.any).isRequired,
  globalSidebarContent: PropTypes.objectOf(PropTypes.any).isRequired,
  productName: PropTypes.string.isRequired,
  isFedrampCompliant: PropTypes.bool,
  footerLinks: PropTypes.shape({
    privacyPolicyLink: PropTypes.string,
    supportLink: PropTypes.string
  }),
  dataTestId: PropTypes.string
};

GlobalSidebarTemplate.defaultProps = {
  isFedrampCompliant: false,
  dataTestId: "orion-globalsidebar-template"
};

GlobalSidebarTemplate.displayName = "OrionGlobalSidebarTemplate";

export default GlobalSidebarTemplate;
